import {ChangeDetectorRef, Component, ElementRef, HostListener, Inject, ViewChild} from '@angular/core';
import {WebmateToolConfig} from "@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service";
import {TOOL_CONTAINER_DATA} from "@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component";

export class ContrastAnalysisViewComponentConfig implements WebmateToolConfig {
    toolName = 'Contrast Analysis';

    constructor(public imageDataUrl: string, public analysisReport: any) {
    }
}
@Component({
  selector: 'wm-contrast-analysis-view',
  templateUrl: './contrast-analysis-view.component.html',
  styleUrls: ['./contrast-analysis-view.component.less']
})
export class ContrastAnalysisViewComponent {
    imageDataUrl: string;
    analysis: [any];

    @ViewChild('canvas') canvasViewChild: ElementRef;
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;

    isTooltipVisible: boolean = true;
    tooltipAnalysisItem: any;

    areBtnsVisible: boolean = false;
    arePassedVisible: boolean = true;
    areFailedVisible: boolean = true;

    canvasImageBlobUrl: string;

    isMagnifierVisible: boolean = false;
    magX: number;
    magY: number;
    magDx: number;
    magDy: number;
    magContW: number;
    magContH: number;

    constructor(
        @Inject(TOOL_CONTAINER_DATA) private config: ContrastAnalysisViewComponentConfig,
        private cdr: ChangeDetectorRef
    ){
        this.imageDataUrl = config.imageDataUrl;
        this.analysis = JSON.parse(config.analysisReport['analysis']).analysis;
    }

    ngAfterViewInit() {
        this.canvas = this.canvasViewChild.nativeElement;
        this.ctx = this.canvas.getContext("2d")!;
        this.drawImageAndAnalysis().then(() => {
            this.snapshotCanvasAsImageBlob().then(() => {
                this.cdr.markForCheck();
            });
        }) ;
    }

    drawImageAndAnalysis(): Promise<void> {
        return new Promise((resolve, _) => {
            let image = new Image();
            image.crossOrigin = "anonymous"; // to avoid CORS issues if the image is from a different origin
            image.onload = () => {
                this.canvas.width = image.width;
                this.canvas.height = image.height;
                // **also set the CSS size to match** so there’s no scaling
                this.canvas.style.width  = `${image.naturalWidth}px`;
                this.canvas.style.height = `${image.naturalHeight}px`;
                this.ctx.drawImage(image, 0, 0);
                this.drawAnalysis();
                resolve();
            };
            image.src = this.imageDataUrl;
        });
    }

    drawAnalysis() {
        this.analysis.forEach(result => {
            let x, y, w, h;
            [x, y, w, h] = [result['textWindow']['x'], result['textWindow']['y'], result['textWindow']['w'], result['textWindow']['h']];
            this.drawTextRect(x, y, w, h, this.normalizeContrastRatio(result['contrastRatio']));
        });
    }

    drawTextRect(x: number, y: number, w: number, h: number, contrastGoodness: number) {
        let strokeColor = "green";
        if (contrastGoodness != 1) {
            strokeColor = "red";
        }
        if ((contrastGoodness == 1 && this.arePassedVisible) || (contrastGoodness != 1 && this.areFailedVisible)) {
          this.drawRect(x, y, w, h, strokeColor);
        }
    }

    drawRect(x: number, y: number, w: number, h: number, color: string) {
        // thin black rect for visibility in cases when color matches the existing background
        this.ctx.beginPath();
        this.ctx.lineWidth = 1.5;
        this.ctx.strokeStyle = "black";
        this.ctx.rect(x - 1, y - 1, w + 2, h + 2);
        this.ctx.stroke();

        this.ctx.beginPath();
        this.ctx.lineWidth = 2.5;
        this.ctx.strokeStyle = color;
        this.ctx.rect(x, y, w, h);
        this.ctx.stroke();
    }

    normalizeContrastRatio(contrastRatio: number) {
        const wcagThreshold = 4.5;
        if (contrastRatio >= 4.5) {
            return 1;
        }
        // contrast ratio is always >= 1
        return (contrastRatio - 1) / wcagThreshold;
    }

    findOverlappingTextBox(x: number, y: number): any {
        return this.analysis.find((elem, i) => {
            const lx = elem['textWindow']['x'];
            const rx = lx + elem['textWindow']['w'];
            const ty = elem['textWindow']['y'];
            const by = ty + elem['textWindow']['h'];
            return lx < x && x < rx && ty < y && y < by;
        });
    }

    mouseInteraction(event: MouseEvent) {
        // getBoundingClientRect() gives the variable size based on the responsiveness of screen
        // whereas the underlying pixel count as per the canvas height & width stays the same
        const rectCanvas = this.canvas.getBoundingClientRect();
        const wx = event.clientX - rectCanvas.left;
        const wy = event.clientY - rectCanvas.top;
        const dx = wx / rectCanvas.width;
        const dy = wy / rectCanvas.height;

        // magnifier glass related
        this.magX = wx;
        this.magY = wy;
        this.magDx = dx;
        this.magDy = dy;
        this.magContW = rectCanvas.width;
        this.magContH = rectCanvas.height;

        if (!this.isMagnifierVisible) {
            const x = dx * this.canvas.width;
            const y = dy * this.canvas.height;
            const overlappingTextBox = this.findOverlappingTextBox(x, y);
            if (!!overlappingTextBox) {
                this.showTooltip(overlappingTextBox);
            } else {
                this.hideTooltip();
            }
        }
    }

    showTooltip(analysisItem: any) {
        this.isTooltipVisible = true;
        this.tooltipAnalysisItem = analysisItem;
    }

    hideTooltip() {
        this.isTooltipVisible = false;
    }

    collapseBtnGrp() {
      this.areBtnsVisible = false;
    }

    revealBtnGrp() {
      this.areBtnsVisible = true;
    }

    handleMagnifierVisibilityChange() {
        if (this.isMagnifierVisible) {
            this.isMagnifierVisible = false;
            this.cdr.markForCheck();
            this.snapshotCanvasAsImageBlob().then(() => {
                this.isMagnifierVisible = true;
                this.isTooltipVisible = false;
                this.cdr.markForCheck();
            });
        }
    }

    snapshotCanvasAsImageBlob(): Promise<void> {
        // Got a very bad performance with canvas.toDataURL() so creating an image blob instead
        return new Promise((resolve, reject) => {
           this.canvas.toBlob((blob) => {
               if (this.canvasImageBlobUrl) {
                   URL.revokeObjectURL(this.canvasImageBlobUrl);
               }
               this.canvasImageBlobUrl = URL.createObjectURL(blob!);
               resolve();
           }, 'image/png');
        });
    }

    @HostListener('document:keydown.M')
    toggleMagnifier() {
        this.isMagnifierVisible = !this.isMagnifierVisible;
        this.handleMagnifierVisibilityChange();
    }
}
